import { itemSizeForMobile } from '../components/utils';
import {
  BY_DEFAULT,
  DECREASE,
  HALF_SIZE,
  INCREASE,
  ROUTE_PROFILE_DETAILS,
  ROUTE_REWARDS_REDEEM,
} from '../constants';
import { persistZero } from '../helpers/helperMethods';
import { Codes } from '../models/item.model';
import {
  PULSE_CLICK_EVENTS_ENABLED,
  PULSE_CLICK_EVENTS_ENABLED_ENV,
} from './pulseConstants';
import {
  PULSE_VIEW_ID,
  profileTabToViewId,
  resolvePhase1RouteViewId,
} from './pulseViewIds';
import { pulse } from './pulseTracker';

/** ARD Category (screen / feature). */
export const PULSE_CLICK_CATEGORY = {
  HOME_PAGE: 'home_page',
  MENU_PAGE: 'menu_page',
  SCAN_PAGE: 'scan_page',
  REWARDS_PAGE: 'rewards_page',
  OFFERS_PAGE: 'offers_page',
  ORDERS_PAGE: 'orders_page',
  CART_PAGE: 'cart_page',
  CHECKOUT_PAGE: 'checkout_page',
  PRODUCT_PAGE: 'product_page',
  CUSTOMER_PAGE: 'customer_page',
  NAVIGATION_PAGE: 'navigation',
  ORDER_TYPE: 'order_type',
  LOCATION_PAGE: 'location_page',
  HAMBURGER_MENU: 'hamburger_menu',
  FAB_MENU: 'fab_menu',
  HEADER: 'header',
  ORDER_MANAGEMENT: 'order_management',
} as const;

/** ARD Action (interaction). */
export const PULSE_CLICK_ACTION = {
  TAP_RECENT_ORDER: 'tap_recent_order',
  TAP_REWARDS_LINK: 'tap_rewards_link',
  TAP_CHANGE_LOCATION: 'tap_change_location',
  TAP_CART_ICON: 'tap_cart_icon',
  TAP_CUSTOMER_MENU: 'tap_customer_menu',
  TAP_CUSTOMER_TAB: 'tap_customer_tab',
  TAP_PROFILE_PREFERENCES: 'tap_profile_preferences',
  TAP_LOYALTY_REWARDS: 'tap_loyalty_rewards',
  TAP_REWARDS_FAQ: 'tap_rewards_faq',
  TAP_HOME_TAB: 'tap_home_tab',
  TAP_MENU_TAB: 'tap_menu_tab',
  TAP_ORDER_TAB: 'tap_order_tab',
  TAP_REWARDS_TAB: 'tap_rewards_tab',
  TAP_SCAN_TAB: 'tap_scan_tab',
  TAP_MENU_CATEGORY: 'tap_menu_category',
  SCAN_QR_CODE: 'scan_qr_code',
  TAP_ADD_RECEIPT_CODE: 'tap_add_receipt_code',
  ENTER_RECEIPT_CODE: 'enter_receipt_code',
  SUBMIT_RECEIPT_CODE: 'submit_receipt_code',
  TAP_ORDER_HISTORY_LINK: 'tap_order_history_link',
  TAP_PAST_REWARDS_BACK: 'tap_past_rewards_back',
  TAP_REDEEMABLE_ITEM: 'tap_redeemable_item',
  ENTER_PROMO_CODE: 'enter_promo_code',
  TAP_CLAIM_PROMO: 'tap_claim_promo',
  TAP_OFFER_LINK: 'tap_offer_link',
  TAP_TOGGLE_FAVORITE: 'tap_toggle_favorite',
  TAP_ORDER_RECEIPT: 'tap_order_receipt',
  TAP_ORDER_AGAIN: 'tap_order_again',
  FAVORITE_NAME_SUBMITTED: 'favorite_name_submitted',
  TAP_CHANGE_QUANTITY: 'tap_change_quantity',
  TAP_DELETE_ITEM: 'tap_delete_item',
  TAP_EDIT_ITEM: 'tap_edit_item',
  TAP_REDEEM_POINTS: 'tap_redeem_points',
  TAP_BACK_TO_MENU: 'tap_back_to_menu',
  TAP_BACK_TO_CART_DETAILS: 'tap_back_to_cart_details',
  TAP_CHECKOUT: 'tap_checkout',
  TAP_YOU_MIGHT_LIKE: 'tap_you_might_like',
  TAP_FREQUENTLY_BOUGHT_TOGETHER: 'tap_frequently_bought_together',
  SELECT_PICKUP_DATE: 'select_pickup_date',
  SELECT_PICKUP_TIME: 'select_pickup_time',
  SELECT_DELIVERY_DATE: 'select_delivery_date',
  SELECT_DELIVERY_TIME: 'select_delivery_time',
  TAP_CONTINUE: 'tap_continue',
  TAP_SWITCH_DELIVERY: 'tap_switch_delivery',
  TAP_SWITCH_PICKUP: 'tap_switch_pickup',
  SELECT_ORDER_TYPE: 'select_order_type',
  EDIT_ORDER_TYPE: 'edit_order_type',
  TAP_SEARCH_LOCATION: 'tap_search_location',
  TAP_STORE_LOCATOR: 'tap_store_locator',
  TAP_CONTACT_US: 'tap_contact_us',
  TAP_SIGN_OUT: 'tap_sign_out',
  TAP_ADD_PAYMENT_METHOD: 'tap_add_payment_method',
  TAP_DELETE_PAYMENT_METHOD: 'tap_delete_payment_method',
  TAP_DELETE_DELIVERY_ADDRESS: 'tap_delete_delivery_address',
  TAP_CHANGE_DEFAULT_DELIVERY_ADDRESS: 'tap_change_default_delivery_address',
  TAP_DELETE_ACCOUNT: 'tap_delete_account',
  TAP_CANCEL_ORDER: 'tap_cancel_order',
  EDIT_DATE_TIME: 'edit_date_time',
  EDIT_STORE_LOCATION: 'edit_store_location',
  EDIT_DELIVERY_LOCATION: 'edit_delivery_location',
  SELECT_TIP_AMOUNT: 'select_tip_amount',
  TAP_ADD_CREDIT_CARD: 'tap_add_credit_card',
  TAP_EDIT_CREDIT_CARD: 'tap_edit_credit_card',
  TAP_ADD_GIFT_CARD: 'tap_add_gift_card',
  TAP_EDIT_GIFT_CARD: 'tap_edit_gift_card',
  TAP_APPLE_PAY: 'tap_apple_pay',
  TAP_GOOGLE_PAY: 'tap_google_pay',
  VIEW_ORDER_TOTAL: 'view_order_total',
  TAP_PLACE_ORDER: 'tap_place_order',
  SELECT_CUSTOM_OPTION: 'select_custom_option',
  SELECT_ADDON: 'select_addon',
  REMOVE_ADDON: 'remove_addon',
  VIEW_PRICE: 'view_price',
  TAP_ADD_TO_CART: 'tap_add_to_cart',
  TAP_QUANTITY_CHANGE_ITEM_DETAIL: 'tap_quantity_change_item_detail',
} as const;

export type TrackPulseClickArgs = {
  category: string;
  action: string;
  label?: string | null;
  value?: string | number | null;
  /** Same `view_id` as the active Pulse view event (defaults from category + path). */
  view_id?: string;
  page_path?: string;
  page_id?: string | null;
  cart_id?: string | number | null;
  additional_params?: Record<string, unknown> | null;
};

/** Default click `view_id` from ARD category and optional pathname. */
export const resolvePulseClickViewId = (
  category: string,
  pathname?: string | null
): string => {
  switch (category) {
    case PULSE_CLICK_CATEGORY.HOME_PAGE:
      return PULSE_VIEW_ID.HOME;
    case PULSE_CLICK_CATEGORY.MENU_PAGE:
      return resolveMenuPageClickViewId(pathname);
    case PULSE_CLICK_CATEGORY.SCAN_PAGE:
      return PULSE_VIEW_ID.SCAN;
    case PULSE_CLICK_CATEGORY.REWARDS_PAGE:
      return PULSE_VIEW_ID.REWARDS;
    case PULSE_CLICK_CATEGORY.OFFERS_PAGE:
      return PULSE_VIEW_ID.REWARDS_OFFERS;
    case PULSE_CLICK_CATEGORY.ORDERS_PAGE: {
      const path = (pathname ?? '').replace(/\/+$/, '');
      if (path === '/profileDetails/more-favorite-orders') {
        return PULSE_VIEW_ID.ORDERS_FAVORITE;
      }
      return PULSE_VIEW_ID.ORDERS_RECENT;
    }
    case PULSE_CLICK_CATEGORY.CART_PAGE:
      return PULSE_VIEW_ID.CART;
    case PULSE_CLICK_CATEGORY.CHECKOUT_PAGE:
    case 'payment_page':
      return PULSE_VIEW_ID.CHECKOUT;
    case PULSE_CLICK_CATEGORY.PRODUCT_PAGE:
      return PULSE_VIEW_ID.PRODUCT;
    case PULSE_CLICK_CATEGORY.CUSTOMER_PAGE:
      return resolveCustomerPageClickViewId(pathname ?? '');
    case PULSE_CLICK_CATEGORY.LOCATION_PAGE:
      return PULSE_VIEW_ID.LOCATIONS;
    case PULSE_CLICK_CATEGORY.FAB_MENU:
      return PULSE_CLICK_CATEGORY.FAB_MENU;
    case PULSE_CLICK_CATEGORY.ORDER_MANAGEMENT:
      return PULSE_VIEW_ID.THANK_YOU;
    default:
      return category;
  }
};

/** Session key: latest profile section `view_id` (mobile tab + desktop scroll). */
export const PULSE_PROFILE_ACTIVE_VIEW_ID_KEY = 'pulseProfileViewId';

/** Active profile section `view_id` for customer-page header clicks on `/profileDetails`. */
export const resolveCustomerPageClickViewId = (
  pathname: string
): string => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path !== ROUTE_PROFILE_DETAILS) {
    return PULSE_VIEW_ID.PROFILE_PERSONAL_INFO;
  }

  if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem(PULSE_PROFILE_ACTIVE_VIEW_ID_KEY);
    if (stored) return stored;

    try {
      const tab = JSON.parse(sessionStorage.getItem('activeTab') ?? '"first"');
      return profileTabToViewId(tab);
    } catch {
      // fall through
    }
  }

  return PULSE_VIEW_ID.PROFILE_PERSONAL_INFO;
};

/** Active profile section `view_id` for `tap_customer_tab` (origin tab, not destination). */
export const resolveCustomerTabClickViewId = (
  pathname?: string | null
): string => {
  const path =
    pathname !== undefined && pathname !== null
      ? pathname
      : typeof window !== 'undefined'
        ? window.location.pathname
        : ROUTE_PROFILE_DETAILS;
  return resolveCustomerPageClickViewId(path);
};

/** ARD tab labels for Profile & Preferences (mobile Tab.Container eventKeys). */
export const profileTabKeyToClickLabel = (
  tabKey: string | null | undefined
): string | null => {
  switch (tabKey) {
    case 'first':
      return 'Profile Info';
    case 'second':
      return 'Payment';
    case 'third':
      return 'Gift Card';
    case 'fourth':
      return 'Delivery';
    case 'eight':
      return 'Your Devices';
    case 'nine':
      return 'Delete Account';
    default:
      return null;
  }
};

/** ARD tab labels for Profile & Preferences (desktop scrollspy section ids). */
export const profileSectionIdToClickLabel = (
  sectionId: string | null | undefined
): string | null => {
  switch (sectionId) {
    case 'Personal_Info':
      return 'Profile Info';
    case 'Payment':
      return 'Payment';
    case 'Gift_Cards':
      return 'Gift Card';
    case 'Delivery_Address':
      return 'Delivery';
    case 'Your_Devices':
      return 'Your Devices';
    case 'Delete_Account':
      return 'Delete Account';
    default:
      return null;
  }
};

/** Customer / profile page clicks (`view_id` = active profile section). */
export const trackCustomerPageClick = ({
  action,
  label = null,
  value = null,
  view_id,
  page_path,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  view_id: string;
  page_path?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.CUSTOMER_PAGE,
    action,
    label,
    value,
    view_id,
    page_id: PULSE_CLICK_CATEGORY.CUSTOMER_PAGE,
    page_path: page_path ?? ROUTE_PROFILE_DETAILS,
  });
};

/** Profile sidebar / tab bar → `tap_customer_tab`. */
export const trackCustomerTabClick = (
  tabKeyOrSectionId: string | null | undefined,
  source: 'mobile_tab' | 'desktop_section'
): void => {
  if (!tabKeyOrSectionId) return;

  const label =
    source === 'mobile_tab'
      ? profileTabKeyToClickLabel(tabKeyOrSectionId)
      : profileSectionIdToClickLabel(tabKeyOrSectionId);

  if (!label) return;

  const page_path =
    typeof window !== 'undefined'
      ? window.location.pathname
      : ROUTE_PROFILE_DETAILS;

  trackCustomerPageClick({
    action: PULSE_CLICK_ACTION.TAP_CUSTOMER_TAB,
    label,
    view_id: resolveCustomerTabClickViewId(page_path),
    page_path,
  });
};

/** Profile → Delete Account section — initial Delete CTA tap. */
export const trackDeleteAccountClick = (page_path?: string): void => {
  trackCustomerPageClick({
    action: PULSE_CLICK_ACTION.TAP_DELETE_ACCOUNT,
    label: null,
    view_id: PULSE_VIEW_ID.PROFILE_DELETE_ACCOUNT,
    page_path,
  });
};

export type BottomNavTabId = 'home' | 'menu' | 'reorder' | 'rewards' | 'scan';

/** ARD labels for mobile bottom navigation tabs. */
export const BOTTOM_NAV_TAB_CLICK_LABELS: Record<BottomNavTabId, string> = {
  home: 'Home',
  menu: 'Menu',
  reorder: 'Order',
  rewards: 'Rewards',
  scan: 'Scan',
};

/** Per-tab ARD actions for mobile bottom navigation. */
export const bottomNavTabToAction = (tabId: BottomNavTabId): string => {
  switch (tabId) {
    case 'home':
      return PULSE_CLICK_ACTION.TAP_HOME_TAB;
    case 'menu':
      return PULSE_CLICK_ACTION.TAP_MENU_TAB;
    case 'reorder':
      return PULSE_CLICK_ACTION.TAP_ORDER_TAB;
    case 'rewards':
      return PULSE_CLICK_ACTION.TAP_REWARDS_TAB;
    case 'scan':
      return PULSE_CLICK_ACTION.TAP_SCAN_TAB;
    default:
      return tabId;
  }
};

/** Current-screen `view_id` for bottom nav tab taps (origin page, not destination tab). */
export const resolveBottomNavClickViewId = (
  pathname?: string | null
): string => {
  const path =
    pathname !== undefined && pathname !== null
      ? pathname
      : typeof window !== 'undefined'
        ? window.location.pathname
        : '/';

  const phase1 = resolvePhase1RouteViewId(path);
  if (phase1) return phase1;

  const normalized = path.replace(/\/+$/, '') || '/';

  if (normalized === '/menu' || normalized.startsWith('/menu/')) {
    return resolveMenuPageClickViewId(path);
  }
  if (normalized === ROUTE_REWARDS_REDEEM) {
    return PULSE_VIEW_ID.REWARDS;
  }
  if (normalized === ROUTE_PROFILE_DETAILS) {
    return resolveCustomerPageClickViewId(path);
  }

  const headerCategory = resolveHeaderPulseClickCategory(path);
  if (headerCategory) {
    return resolvePulseClickViewId(headerCategory, path);
  }

  return PULSE_VIEW_ID.HOME;
};

/** Active menu screen view_id from pathname (`view_menu`, `category_menu`, or combo `item_detail`). */
export const resolveMenuPageClickViewId = (
  pathname?: string | null
): string => {
  const path = (pathname ?? '').replace(/\/+$/, '');
  if (path === '/menu') {
    return PULSE_VIEW_ID.MENU;
  }
  if (path.startsWith('/menu/')) {
    const segment = decodeURIComponent(path.slice('/menu/'.length));
    const looksLikeComboTab =
      /try\s*2/i.test(segment) || /kid'?s?\s*combo/i.test(segment);
    if (looksLikeComboTab) {
      return PULSE_VIEW_ID.ITEM_DETAIL;
    }
    return PULSE_VIEW_ID.CATEGORY_MENU;
  }
  if (!path) {
    if (pathname === '/') {
      return resolveBottomNavClickViewId(pathname);
    }
    return PULSE_VIEW_ID.MENU;
  }
  return resolveBottomNavClickViewId(pathname);
};

/** Menu page clicks (category tabs, category list tiles). */
export const trackMenuPageClick = ({
  action,
  label = null,
  value = null,
  page_path,
  view_id,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  page_path?: string;
  view_id?: string;
}): void => {
  const path =
    page_path !== undefined
      ? page_path
      : typeof window !== 'undefined'
        ? window.location.pathname
        : undefined;

  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.MENU_PAGE,
    action,
    label,
    value,
    view_id: view_id ?? resolveBottomNavClickViewId(path),
    page_id: PULSE_CLICK_CATEGORY.MENU_PAGE,
    page_path: path,
  });
};

/** Mobile bottom nav tab clicks (`tap_home_tab`, `tap_menu_tab`, …). */
export const trackBottomNavTabClick = (
  tabId: BottomNavTabId,
  { page_path }: { page_path?: string } = {}
): void => {
  const path =
    page_path !== undefined
      ? page_path
      : typeof window !== 'undefined'
        ? window.location.pathname
        : undefined;

  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.NAVIGATION_PAGE,
    action: bottomNavTabToAction(tabId),
    label: BOTTOM_NAV_TAB_CLICK_LABELS[tabId],
    view_id: resolveBottomNavClickViewId(path),
    page_id: PULSE_CLICK_CATEGORY.NAVIGATION_PAGE,
    page_path: path,
  });
};

/** Code + env kill switch for click events (views unaffected). */
export const isPulseClickEventsEnabled = (): boolean => {
  if (!PULSE_CLICK_EVENTS_ENABLED) return false;
  const env = process.env[PULSE_CLICK_EVENTS_ENABLED_ENV];
  if (env === undefined || env === '') return true;
  return String(env).toLowerCase() === 'true';
};

/**
 * Fire a Pulse `click` with ARD category / action / label.
 * No-ops when click events or the master tracker are disabled.
 */
export const trackPulseClick = ({
  category,
  action,
  label = null,
  value = null,
  view_id,
  page_path,
  page_id,
  cart_id = null,
  additional_params = null,
}: TrackPulseClickArgs): void => {
  if (!isPulseClickEventsEnabled()) return;

  const path =
    page_path !== undefined
      ? page_path
      : typeof window !== 'undefined'
        ? window.location.pathname
        : null;

  const resolvedViewId =
    view_id ?? resolvePulseClickViewId(category, path);

  pulse.click({
    category,
    action,
    label,
    view_id: resolvedViewId,
    cart_id,
    ...(additional_params !== null && additional_params !== undefined
      ? { additional_params }
      : {}),
    ...(path !== null && path !== undefined ? { page_path: path } : {}),
    ...(page_id !== undefined ? { page_id } : {}),
  });
};

/** Header location/cart clicks: page category from pathname (+ rewards tab on /rewards). */
export const resolveHeaderPulseClickCategory = (
  pathname: string,
  rewardsCategory: string | null = null
): (typeof PULSE_CLICK_CATEGORY)[keyof typeof PULSE_CLICK_CATEGORY] | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return PULSE_CLICK_CATEGORY.HOME_PAGE;
  if (path === '/menu' || path.startsWith('/menu/')) {
    return PULSE_CLICK_CATEGORY.MENU_PAGE;
  }
  if (path === '/scan') return PULSE_CLICK_CATEGORY.SCAN_PAGE;
  if (path === '/rewards') {
    return rewardsCategory === PULSE_CLICK_CATEGORY.OFFERS_PAGE
      ? PULSE_CLICK_CATEGORY.OFFERS_PAGE
      : PULSE_CLICK_CATEGORY.REWARDS_PAGE;
  }
  if (
    path === '/profileDetails/more-recent-orders' ||
    path === '/profileDetails/more-favorite-orders'
  ) {
    return PULSE_CLICK_CATEGORY.ORDERS_PAGE;
  }
  if (path === ROUTE_PROFILE_DETAILS) {
    return PULSE_CLICK_CATEGORY.CUSTOMER_PAGE;
  }
  return null;
};

/** Dedicated Recent / Favorite orders routes (`/profileDetails/more-*-orders`). */
export const isOrdersPulsePagePath = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '');
  return (
    path === '/profileDetails/more-recent-orders' ||
    path === '/profileDetails/more-favorite-orders'
  );
};

/** Orders click label: prefer order id, then item name. */
export const ordersClickLabel = (order: {
  id?: number | string | null;
  item_name?: string | null;
  items?: Array<{ name?: string } | null> | null;
} | null | undefined): string => {
  if (order?.id != null && order.id !== '') return String(order.id);
  if (order?.item_name) return String(order.item_name);
  const fromItems = (order?.items || [])
    .map((item) => item?.name)
    .filter(Boolean)
    .join(', ');
  return fromItems || 'Order';
};

/** Cart modal clicks (category always cart_page). */
export const trackCartPageClick = ({
  action,
  label = null,
  value = null,
  page_path,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  page_path?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.CART_PAGE,
    action,
    label,
    value,
    view_id: PULSE_VIEW_ID.CART,
    page_id: PULSE_CLICK_CATEGORY.CART_PAGE,
    page_path,
  });
};

/** Cart "You might also like" upsell — Add button. */
export const trackCartYouMightLikeClick = ({
  label,
  page_path,
  additional_params,
}: {
  label: string;
  page_path?: string;
  additional_params: Record<string, string | number>;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.CART_PAGE,
    action: PULSE_CLICK_ACTION.TAP_YOU_MIGHT_LIKE,
    label,
    view_id: PULSE_VIEW_ID.CART,
    page_id: PULSE_CLICK_CATEGORY.CART_PAGE,
    page_path,
    additional_params,
  });
};

/** Checkout flow clicks (date/time slot modal, category checkout_page). */
export const trackCheckoutPageClick = ({
  action,
  label = null,
  value = null,
  page_path,
  view_id = PULSE_VIEW_ID.CHECKOUT,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  page_path?: string;
  view_id?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
    action,
    label,
    value,
    view_id,
    page_id: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
    page_path,
  });
};

/** Checkout finalize clicks on `/checkout` (tip, payment, place order, edits). */
export const trackPaymentPageClick = ({
  action,
  label = null,
  value = null,
  page_path,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  page_path?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
    action,
    label,
    value,
    view_id: PULSE_VIEW_ID.CHECKOUT,
    page_id: PULSE_CLICK_CATEGORY.CHECKOUT_PAGE,
    page_path,
  });
};

/** Added-to-cart modal upsell — Frequently bought together Add button. */
export const trackProductPageFrequentlyBoughtTogetherClick = ({
  label,
  page_path,
  additional_params,
}: {
  label: string;
  page_path?: string;
  additional_params: Record<string, string | number>;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    action: PULSE_CLICK_ACTION.TAP_FREQUENTLY_BOUGHT_TOGETHER,
    label,
    view_id: PULSE_VIEW_ID.PRODUCT,
    page_id: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    page_path,
    additional_params,
  });
};

/** Item detail / product page clicks (`view_id: item_detail`). */
export const trackProductPageClick = ({
  action,
  label = null,
  value = null,
  page_path,
}: {
  action: string;
  label?: string | null;
  value?: string | number | null;
  page_path?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    action,
    label,
    value,
    view_id: PULSE_VIEW_ID.PRODUCT,
    page_id: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    page_path,
  });
};

/** Item detail quantity +/- tap. */
export const trackProductPageQuantityChangeClick = ({
  label,
  item_id,
  item_name,
  old_quantity,
  new_quantity,
  page_path,
}: {
  label: string;
  item_id: string | number;
  item_name: string;
  old_quantity: number;
  new_quantity: number;
  page_path?: string;
}): void => {
  trackPulseClick({
    category: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    action: PULSE_CLICK_ACTION.TAP_QUANTITY_CHANGE_ITEM_DETAIL,
    label,
    view_id: PULSE_VIEW_ID.PRODUCT,
    page_id: PULSE_CLICK_CATEGORY.PRODUCT_PAGE,
    page_path,
    additional_params: {
      item_id,
      item_name,
      old_quantity,
      new_quantity,
    },
  });
};

/** ARD price label without currency symbol (e.g. `8.99`). */
export const formatProductPagePriceLabel = (
  price: number | string | null | undefined
): string | null => {
  const num = typeof price === 'string' ? parseFloat(price) : Number(price);
  if (!Number.isFinite(num) || num <= 0) return null;
  return String(persistZero(num));
};

/** Size label for `select_custom_option` (Half / Large / etc.). */
export const resolveProductSizeClickLabel = (
  sizeId: string,
  categoryName?: string | null,
  categoryId?: string | number | null
): string => {
  const normalized = (sizeId ?? '').toUpperCase();
  const sizeKey = normalized === HALF_SIZE.id ? 'HALF' : 'FULL';
  return (
    itemSizeForMobile(
      sizeKey,
      categoryName ?? '',
      categoryId?.toString() ?? ''
    ) ?? normalized
  );
};

/** Fire `select_addon` / `remove_addon` for any modifier group interaction (core, required, add-ons, complementary, substitute). */
export const trackProductPageModifierClick = (
  data: {
    modifier_type?: string;
    modifier_name?: string;
    type?: string;
    code?: string;
  },
  page_path?: string
): void => {
  if (!data?.modifier_name) return;
  if (data.type === BY_DEFAULT) return;

  const isSelect =
    data.type === INCREASE || data.code === Codes.ADD;
  const isRemove =
    data.type === DECREASE || data.code === Codes.NO;

  if (isSelect && !isRemove) {
    trackProductPageClick({
      action: PULSE_CLICK_ACTION.SELECT_ADDON,
      label: data.modifier_name,
      page_path,
    });
    return;
  }

  if (isRemove && !isSelect) {
    trackProductPageClick({
      action: PULSE_CLICK_ACTION.REMOVE_ADDON,
      label: data.modifier_name,
      page_path,
    });
  }
};

/** Web combines order type + date/time into one Edit control — fire both ARD actions. */
export const trackPaymentOrderTypeAndTimeEdit = (page_path?: string): void => {
  trackPaymentPageClick({
    action: PULSE_CLICK_ACTION.EDIT_ORDER_TYPE,
    page_path,
  });
  trackPaymentPageClick({
    action: PULSE_CLICK_ACTION.EDIT_DATE_TIME,
    page_path,
  });
};

/** Home recent-order card label from order payload (item names). */
export const recentOrderClickLabel = (order: {
  items?: Array<{ name?: string } | null> | null;
  items_with_quantity?: string | null;
  favorite_order_name?: string | null;
  name?: string | null;
} | null | undefined): string => {
  const fromItems = (order?.items || [])
    .map((item) => item?.name)
    .filter(Boolean)
    .join(', ');
  if (fromItems) return fromItems;
  if (order?.items_with_quantity) return String(order.items_with_quantity);
  if (order?.favorite_order_name) return String(order.favorite_order_name);
  if (order?.name) return String(order.name);
  return 'Recent order';
};
